import { useState } from "react";
import type { Workshop } from "@/content/workshops";
import {
  modalContentByWorkshop,
  type QuestionnaireContent,
  type QuestionnaireQuestion,
} from "@/features/workshop/modals/modal-content";
import { resolveField } from "../workshop-override-content";
import {
  clearEffectiveOverrideField,
  setEffectiveOverride,
  useEffectiveWorkshopOverride,
} from "@/stores/useEffectiveWorkshopOverride";
import {
  AddButton,
  Field,
  ListCard,
  SectionFrame,
  Select,
  TextArea,
  TextInput,
} from "../editor-kit";
import { genId, moveItem, sameJson } from "../editor-utils";

/** Questionnaire ("the questions") editor — intro + a list of questions. */
export function QuestionnaireEditor({ wsId }: { wsId: Workshop["id"] }) {
  const override = useEffectiveWorkshopOverride(wsId);
  const def = modalContentByWorkshop[wsId].questionnaire;
  const current = resolveField(override, "questionnaire", def);
  const [draft, setDraft] = useState<QuestionnaireContent>(current);

  const setQuestions = (questions: QuestionnaireQuestion[]) =>
    setDraft((d) => ({ ...d, questions }));
  const setQuestion = (i: number, patch: Partial<QuestionnaireQuestion>) =>
    setQuestions(draft.questions.map((q, j) => (j === i ? { ...q, ...patch } : q)));

  return (
    <SectionFrame
      description="The pre-workshop questionnaire shown to participants."
      overridden={override?.questionnaire !== undefined}
      dirty={!sameJson(draft, current)}
      onSave={() => setEffectiveOverride(wsId, { questionnaire: draft })}
      onReset={() => {
        clearEffectiveOverrideField(wsId, "questionnaire");
        setDraft(def);
      }}
    >
      <Field label="Intro">
        <TextArea value={draft.intro} onChange={(e) => setDraft((d) => ({ ...d, intro: e.target.value }))} />
      </Field>

      {draft.questions.map((q, i) => (
        <ListCard
          key={q.id}
          title={`Question ${i + 1}`}
          onMoveUp={i > 0 ? () => setQuestions(moveItem(draft.questions, i, -1)) : undefined}
          onMoveDown={
            i < draft.questions.length - 1
              ? () => setQuestions(moveItem(draft.questions, i, 1))
              : undefined
          }
          onRemove={() => setQuestions(draft.questions.filter((_, j) => j !== i))}
        >
          <Field label="Question">
            <TextInput value={q.label} onChange={(e) => setQuestion(i, { label: e.target.value })} />
          </Field>
          <div className="grid grid-cols-[auto_1fr] gap-2">
            <Field label="Input type">
              <Select
                value={q.type}
                onChange={(v) => setQuestion(i, { type: v as QuestionnaireQuestion["type"] })}
                options={[
                  { value: "input", label: "single line" },
                  { value: "textarea", label: "multi-line" },
                  { value: "choice", label: "single choice" },
                  { value: "scale", label: "scale (1–5)" },
                ]}
              />
            </Field>
            <Field label="Placeholder">
              <TextInput
                value={q.placeholder ?? ""}
                onChange={(e) => setQuestion(i, { placeholder: e.target.value })}
              />
            </Field>
          </div>
          {q.type === "choice" && (
            <Field label="Options (one per line)">
              <TextArea
                value={(q.options ?? []).join("\n")}
                onChange={(e) =>
                  setQuestion(i, {
                    options: e.target.value
                      .split("\n")
                      .map((o) => o.trim())
                      .filter((o) => o.length > 0),
                  })
                }
              />
            </Field>
          )}
        </ListCard>
      ))}
      <AddButton
        label="Add question"
        onClick={() =>
          setQuestions([
            ...draft.questions,
            { id: genId("q"), label: "", type: "input", placeholder: "" },
          ])
        }
      />
    </SectionFrame>
  );
}
